import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';
import cultivarService from '../../services/cultivar';
import { formatDateTimeInTz, formatLocalDateInTz } from '../../utils/timezone';

interface VerifiedLicense {
  id: number;
  license_tier: string;
  status: string;
  signed_at: string | null;
  signer_name: string | null;
  signature_method: string | null;
  max_trees: number | null;
  region: string | null;
  start_date: string | null;
  end_date: string | null;
  content_hash: string | null;
  template_version: number | null;
  contract_name: string | null;
  cultivar_name: string | null;
  cultivar_trade_name: string | null;
  licensor_org_name: string | null;
  licensee_org_name: string | null;
  supplying_nursery_name: string | null;
}

const TIER: Record<string, string> = {
  nursery_master_sublicence: 'Master nursery sublicence',
  nursery_propagation: 'Nursery propagation licence',
  orchard_planting: 'Orchard planting licence',
};

// Public — reached from the QR code on every licence PDF. Nobody reading it is
// signed in, so dates are shown in the licence's home timezone.
const TZ = 'Australia/Melbourne';

/**
 * Who holds this licence, whether it is in force, and the fingerprint of its
 * text. Anyone holding a printed copy can compare the fingerprint on the page
 * with the one here: if they match, the copy is the text that was signed.
 */
export default function LicenseVerify() {
  const { token } = useParams<{ token: string }>();
  const [lic, setLic] = useState<VerifiedLicense | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    cultivarService.verifyLicense(token)
      .then((res: { success: boolean; data?: VerifiedLicense }) => {
        if (res.success && res.data) setLic(res.data);
        else setError('This licence could not be found.');
      })
      .catch(() => setError('This licence could not be found.'));
  }, [token]);

  if (!lic && !error) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  }

  const inForce = lic?.status === 'active';
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-xl mx-auto bg-white border border-neutral-200 rounded-xl p-6 space-y-4">
        {error || !lic ? (
          <div className="text-center space-y-2">
            <ShieldAlert className="h-12 w-12 text-red-500 mx-auto" />
            <p className="text-neutral-800">{error}</p>
          </div>
        ) : (
          <>
            <div className="text-center space-y-1">
              {inForce
                ? <ShieldCheck className="h-12 w-12 text-emerald-500 mx-auto" />
                : <ShieldAlert className="h-12 w-12 text-amber-500 mx-auto" />}
              <h1 className="text-xl font-bold text-neutral-900">{TIER[lic.license_tier] || 'Cultivar licence'} #{lic.id}</h1>
              <p className={`text-sm font-semibold ${inForce ? 'text-emerald-700' : 'text-amber-700'}`}>
                {inForce ? 'In force' : `Not in force — ${lic.status.replace(/_/g, ' ')}`}
              </p>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-neutral-500">Licensor</dt><dd className="break-words">{lic.licensor_org_name}</dd>
              <dt className="text-neutral-500">Licensee</dt><dd className="break-words">{lic.licensee_org_name}</dd>
              {(lic.cultivar_trade_name || lic.cultivar_name) && (<>
                <dt className="text-neutral-500">Cultivar</dt><dd className="break-words">{lic.cultivar_trade_name || lic.cultivar_name}</dd>
              </>)}
              {lic.supplying_nursery_name && (<>
                <dt className="text-neutral-500">Supplying nursery</dt><dd className="break-words">{lic.supplying_nursery_name}</dd>
              </>)}
              {lic.region && (<><dt className="text-neutral-500">Region</dt><dd>{lic.region}</dd></>)}
              {lic.max_trees != null && (<><dt className="text-neutral-500">Trees</dt><dd>{lic.max_trees}</dd></>)}
              {lic.start_date && (<><dt className="text-neutral-500">From</dt><dd>{formatLocalDateInTz(String(lic.start_date).slice(0, 10), TZ)}</dd></>)}
              {lic.end_date && (<><dt className="text-neutral-500">To</dt><dd>{formatLocalDateInTz(String(lic.end_date).slice(0, 10), TZ)}</dd></>)}
              {lic.signed_at && (<>
                <dt className="text-neutral-500">Signed</dt>
                <dd>{formatDateTimeInTz(lic.signed_at, TZ)}{lic.signer_name ? ` by ${lic.signer_name}` : ''}</dd>
              </>)}
              {lic.contract_name && (<>
                <dt className="text-neutral-500">Contract</dt>
                <dd className="break-words">{lic.contract_name}{lic.template_version ? ` — version ${lic.template_version}` : ''}</dd>
              </>)}
            </dl>
            {lic.content_hash && (
              <div className="bg-neutral-50 border border-neutral-200 rounded p-3">
                <p className="text-xs text-neutral-500 mb-1">Text fingerprint (SHA-256). It matches the one printed on a genuine copy of this licence.</p>
                <p className="font-mono text-xs break-all text-neutral-900">{lic.content_hash}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
